import React, { useState } from 'react'
import { Image, ImageStyle, TouchableOpacity, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'

import { Action } from '@trivie/core/src/models/workflow/Action'

import { Text } from '../text'

import { color } from '../../theme/color'
import { BorderGradient } from './border-gradient'
import { Node } from '@trivie/core/src/models/workflow/Node'
import { useTranslation } from 'react-i18next'
import { Medallion } from '../medallion'
import { useStores } from '@trivie/core'
import { CertificationModal } from '../certification-modal'

export interface PathActionProps {
  action: Instance<typeof Action>
  goToBadge?: any
  node: Instance<typeof Node>
}

export const PathAction = observer((props: PathActionProps) => {
  const { goToBadge, action, node, ...rest } = props
  const { userStore } = useStores()
  const { getCertificationDetail, user, certificationDetail } = userStore

  const { t } = useTranslation()

  const [selectedCert, setSelectedCert] = useState<any>()

  const translateLabel = () => {
    if (action) {
      const { label, is_completed } = action
      if (label === 'Award Badge') {
        return `${is_completed ? t('Notifications.Badges.badge_earned') : t('Path.earnBadge')}`
      } else if (label === 'Add User to Assignment') {
        return t('Path.addToAssignment')
      } else if (label === 'Add User to Group') {
        return t('Path.addToGroup')
      } else if (label === 'Untag User') {
        return t('Path.untagUser')
      } else if (label === 'Tag User') {
        return t('Path.tagUser')
      }
      return action.label
    }
    return ''
  }

  const onPressCert = (cert: any) => {
    if (user && cert) {
      getCertificationDetail(user?.id, cert.certification_id).then((resp) => {
        setSelectedCert(cert)
      })
    }
  }

  return (
    <>
      <TouchableOpacity
        disabled={!action.is_completed}
        onPress={
          action.action_type === 'award_certification'
            ? () => onPressCert(action.cert)
            : action.action_type === 'award_badge'
              ? () => goToBadge(action.badge)
              : () => {}
        }
        {...rest}
      >
        <BorderGradient
          borderRadius={20}
          style={{ height: 72 }}
          locations={[0, 0.5, 1]}
          useAngle
          angle={24}
          angleCenter={{ x: 0.5, y: 0.5 }}
        >
          <View style={AWARD}>
            {action.action_type === 'award_badge' ? (
              <Image
                style={{ ...BADGE_ICON, opacity: !node.locked() && action?.is_completed ? 1 : 0.5 }}
                source={{ uri: action?.badge?.icon_url }}
              />
            ) : null}
            {action.action_type === 'award_certification' ? (
              <View style={{ left: -26 }}>
                <Medallion
                  style={{
                    zIndex: 9999,
                    position: 'relative',
                    opacity: action?.is_completed && action?.cert ? 1 : 0.15,
                  }}
                  type={action.is_completed && action?.cert ? action?.cert?.level : 'Silver'}
                  recall={action?.is_completed ? 1 : 0}
                  scale={0.5}
                />
              </View>
            ) : null}
            <View
              style={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                display: 'flex',
                left: action.action_type === 'award_certification' ? -40 : 0,
              }}
            >
              <Text
                style={{
                  color: color.shade70,
                  opacity: node.locked() || !action.is_completed ? 0.4 : 1,
                }}
                text={translateLabel()}
                variant="titleEmphasized"
              />
              {action?.action_type === 'award_badge' ? (
                <Text
                  text={action.badge?.label}
                  variant="body2Medium"
                  style={{
                    color: color.shade50,
                    marginTop: 2,
                    opacity: node.locked() || !action.is_completed ? 0.4 : 1,
                  }}
                />
              ) : null}
              {action.description && action?.action_type !== 'award_badge' ? (
                <Text
                  text={action.description}
                  variant="body2Medium"
                  style={{
                    color: color.shade50,
                    marginTop: 2,
                    opacity: node.locked() || !action.is_completed ? 0.4 : 1,
                  }}
                />
              ) : null}
            </View>
          </View>
        </BorderGradient>
      </TouchableOpacity>
      <CertificationModal
        visible={Boolean(selectedCert)}
        onClose={() => setSelectedCert(null)}
        title={selectedCert?.title}
        level={selectedCert?.level}
        criteria={certificationDetail?.criteria_met ?? ''}
        earned={certificationDetail?.datetime_awarded ?? ''}
        recall={selectedCert?.recall}
      />
    </>
  )
})

/**
 * Styling
 */
const ACTION: ViewStyle = {
  height: 60,
  borderWidth: 1,
  borderColor: color.shade20,
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}
const AWARD: ViewStyle = {
  ...ACTION,
  height: 70,
  borderWidth: 0,
  width: '100%',
  paddingVertical: 12,
  paddingHorizontal: 20,
}
const BADGE_ICON: ImageStyle = { height: 48, width: 48, borderRadius: 100, marginRight: 12 }
